import React, { useState, useEffect, useRef } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Icon } from '@components/icons';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import { useLanguage } from '@components/context/language';
import { getTranslation } from '@translations';
import { getLanguageFromPath } from '@utils';

const StyledProjectsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  h2 {
    font-size: clamp(24px, 5vw, var(--fz-heading));
  }

  .archive-link {
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    &:after {
      bottom: 0.1em;
    }
  }

  .projects-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    grid-gap: 15px;
    position: relative;
    margin-top: 50px;

    @media (max-width: 1080px) {
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    }
  }

  .more-button {
    ${({ theme }) => theme.mixins.button};
    margin: 80px auto 0;
  }
`;

const StyledProject = styled.div`
  cursor: default;
  transition: var(--transition);

  &:hover,
  &:focus {
    outline: 0;
    .project-inner {
      transform: translateY(-5px);
    }
  }

  .project-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    ${({ theme }) => theme.mixins.flexBetween};
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    height: 100%;
    padding: 2rem 1.75rem;
    border-radius: var(--border-radius);
    background-color: var(--john-bg-mild);
    transition: var(--transition);
  }

  .project-top {
    ${({ theme }) => theme.mixins.flexBetween};
    margin-bottom: 30px;

    .folder {
      color: var(--john-text-pretty);
      svg {
        width: 40px;
        height: 40px;
      }
    }

    .project-links {
      margin-right: -10px;
      color: var(--john-text-mild);

      a {
        padding: 5px 10px;

        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .project-title {
    margin: 0 0 10px;
    color: var(--john-text-mildest);
    font-size: var(--fz-xxl);
  }

  .project-description {
    color: var(--john-text-mild);
    font-size: 17px;

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  .project-tech-list {
    display: flex;
    align-items: flex-end;
    flex-grow: 1;
    flex-wrap: wrap;
    padding: 0;
    margin: 20px 0 0 0;
    list-style: none;

    li {
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      line-height: 1.75;

      &:not(:last-of-type) {
        margin-right: 15px;
      }
    }
  }
`;

const Projects = () => {
  const { language } = useLanguage();

  const data = useStaticQuery(graphql`
    query {
      projects: allMdx(
        filter: {
          fileAbsolutePath: { regex: "/projects/" }
          frontmatter: { showInProjects: { ne: false } }
        }
        sort: { fields: [frontmatter___priority], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              date
              title
              tech
              github
              external
              blog
            }
            body
            fileAbsolutePath
          }
        }
      }
    }
  `);

  // Group projects by base name and select appropriate language version
  const projectsByName = data.projects.edges.reduce((acc, { node }) => {
    // Extract base name from path (e.g., /projects/Bacuda.md -> 'Bacuda')
    const pathParts = node.fileAbsolutePath.split('/');
    const filename = pathParts[pathParts.length - 1];

    // Extract base name by removing extensions (supports .md and .mdx)
    const baseName = filename.replace(/\.([a-z]{2})\.mdx?$/, '').replace(/\.mdx?$/, '');

    const fileLang = getLanguageFromPath(node.fileAbsolutePath);

    if (!acc[baseName]) {
      acc[baseName] = {};
    }

    acc[baseName][fileLang] = node;
    return acc;
  }, {});

  // Create projects data with language-specific content
  const projects = Object.keys(projectsByName)
    .map(projectName => {
      const projectVersions = projectsByName[projectName];
      // Prefer current language, fallback to English
      const selectedProject = projectVersions[language] || projectVersions.en;

      if (!selectedProject) {
        console.warn(`No project found for ${projectName}`);
        return null;
      }

      return { node: selectedProject };
    })
    .filter(Boolean) // Remove null entries
    .sort(
      (a, b) =>
        // Keep priority-based sorting from GraphQL query
        a.node.frontmatter.priority - b.node.frontmatter.priority,
    );

  const [showMore, setShowMore] = useState(false);
  const revealTitle = useRef(null);
  const revealArchiveLink = useRef(null);
  const revealProjects = useRef([]);

  useEffect(() => {
    sr.reveal(revealTitle.current, srConfig());
    sr.reveal(revealArchiveLink.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  const GRID_LIMIT = 6;
  const firstSix = projects.slice(0, GRID_LIMIT);
  const projectsToShow = showMore ? projects : firstSix;

  return (
    <StyledProjectsSection key={language}>
      <h2 ref={revealTitle}>{getTranslation(language, 'projects.other')}</h2>

      <div style={{ display: 'flex', gap: '3rem' }} ref={revealArchiveLink}>
        <Link className="inline-link archive-link" to="/archive">
          🚀{getTranslation(language, 'projects.exploreAll')}
        </Link>
        <Link className="inline-link archive-link" to="/blog">
          📝{getTranslation(language, 'projects.readWriting')}
        </Link>
      </div>

      <TransitionGroup className="projects-grid">
        {projectsToShow &&
          projectsToShow.map(({ node }, i) => {
            const { frontmatter, body } = node;
            const { github, external, title, tech, blog } = frontmatter;

            return (
              <CSSTransition
                key={i}
                classNames="fadeup"
                timeout={i >= GRID_LIMIT ? (i - GRID_LIMIT) * 300 : 300}
                exit={false}
              >
                <StyledProject
                  key={i}
                  ref={el => (revealProjects.current[i] = el)}
                  tabIndex="0"
                  style={{
                    transitionDelay: `${i >= GRID_LIMIT ? (i - GRID_LIMIT) * 100 : 0}ms`,
                  }}
                >
                  <div className="project-inner">
                    <header>
                      <div className="project-top">
                        <div className="folder">
                          <Icon name="Folder" />
                        </div>
                        <div className="project-links">
                          {github && (
                            <a
                              href={github}
                              aria-label="GitHub Link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Icon name="GitHub" />
                            </a>
                          )}
                          {external && (
                            <a
                              href={external}
                              aria-label="External Link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Icon name="External" />
                            </a>
                          )}
                          {blog && (
                            <a
                              href={blog}
                              aria-label="Blog Link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Icon name="Blog" />
                            </a>
                          )}
                        </div>
                      </div>

                      <a href={blog ? blog : external ? external : github ? github : '#'}>
                        <h3 className="project-title">{title}</h3>
                      </a>
                      <div className="project-description">
                        <MDXRenderer>{body}</MDXRenderer>
                      </div>
                    </header>
                    <footer>
                      {tech && tech.length > 0 && (
                        <ul className="project-tech-list">
                          {tech.map((tech, i) => (
                            <li key={i}>{tech}</li>
                          ))}
                        </ul>
                      )}
                    </footer>
                  </div>
                </StyledProject>
              </CSSTransition>
            );
          })}
      </TransitionGroup>

      <button className="more-button" onClick={() => setShowMore(!showMore)}>
        {getTranslation(language, showMore ? 'projects.showLess' : 'projects.showMore')}
      </button>
    </StyledProjectsSection>
  );
};

export default Projects;
